import { BOOKING_ONBOARDING_BOOKING_TYPE_OPTIONS, BOOKING_ONBOARDING_VALID_TIMEZONE_CODES } from '@/constants/bookingOnboarding';
import i18n from '@/utils/lenguage/config';
import { matchIsValidTel, type MuiTelInputCountry } from 'mui-tel-input';
import { SchemaOf, mixed, object, string } from 'yup';
import { BookingOnboardingFormValues } from './types';

const validationMessage = (key: string) => (): string => i18n.t(key);

const positiveIntegerField = (requiredKey: string): SchemaOf<string> =>
    string()
        .trim()
        .required(validationMessage(requiredKey))
        .matches(/^[1-9]\d*$/, {
            message: validationMessage('bookingOnboarding.validation.positiveInteger'),
            excludeEmptyString: true
        });

export const bookingOnboardingValidationSchema: SchemaOf<BookingOnboardingFormValues> = object({
    restaurantName: string().trim().required(validationMessage('bookingOnboarding.validation.restaurantNameRequired')),
    email: string()
        .trim()
        .required(validationMessage('bookingOnboarding.validation.emailRequired'))
        .email(validationMessage('bookingOnboarding.validation.emailInvalid')),
    contactCountry: mixed<MuiTelInputCountry>().required(validationMessage('bookingOnboarding.validation.contactCountryRequired')),
    contact: string()
        .trim()
        .required(validationMessage('bookingOnboarding.validation.contactRequired'))
        .test('valid-contact-number-for-country', validationMessage('bookingOnboarding.validation.contactInvalid'), function (value) {
            if (!value) return true;

            const contactCountry = this.parent.contactCountry as MuiTelInputCountry | undefined;
            if (!contactCountry) return false;

            return matchIsValidTel(value, { onlyCountries: [contactCountry] });
        }),
    ownerManagerName: string().trim().required(validationMessage('bookingOnboarding.validation.ownerManagerNameRequired')),
    diningDuration: positiveIntegerField('bookingOnboarding.validation.diningDurationRequired'),
    bookingSlotInterval: positiveIntegerField('bookingOnboarding.validation.bookingSlotIntervalRequired'),
    minimumAdvanceBookingTime: positiveIntegerField('bookingOnboarding.validation.minimumAdvanceBookingTimeRequired'),
    sessionMaxCapacity: positiveIntegerField('bookingOnboarding.validation.sessionMaxCapacityRequired'),
    timezone: string()
        .trim()
        .required(validationMessage('bookingOnboarding.validation.timezoneRequired'))
        .test('valid-timezone', validationMessage('bookingOnboarding.validation.timezoneInvalid'), (value) => {
            if (!value) return true;

            return BOOKING_ONBOARDING_VALID_TIMEZONE_CODES.includes(value);
        }),
    bookingType: mixed<BookingOnboardingFormValues['bookingType']>()
        .oneOf([...BOOKING_ONBOARDING_BOOKING_TYPE_OPTIONS], validationMessage('bookingOnboarding.validation.bookingTypeRequired'))
        .required(validationMessage('bookingOnboarding.validation.bookingTypeRequired')),
    maxPaxPerBooking: positiveIntegerField('bookingOnboarding.validation.maxPaxPerBookingRequired')
});
